import React, { useState, useRef, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Paper, Button, Box, Grid, Typography, CircularProgress } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import IconButton from '@mui/material/IconButton';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getTranscript } from "../../api/transcriptApi";
import { merge } from '../utils/transcriptUtils';
import { getTimestampFromDuration } from "../utils/timestampUtils";
import { insertTimestamp, getSections } from './bookmarks';
import { StyledH5Heading } from '../style/StyledComponents';

function findLastSmallerOffset(dictList, field, value) {
  let low = 0;
  let high = dictList.length - 1;
  let result = -1;

  while (low <= high) {
    let mid = Math.floor((low + high) / 2);
    if (dictList[mid][field] < value) {
      result = mid;  // Store the last known index where condition is true
      low = mid + 1; // Continue to check the right half
    } else {
      high = mid - 1; // Check the left half
    }
  }

  return result; // This will return -1 if no elements are smaller than time

}

function TranscriptLoader({
  embedId,
  transcript, 
  setTranscript,
  transcriptState,
  setTranscriptState
}) {
  const [loading, setLoading] = useState(false);

  const handleLoadTranscript = () => {
    if (embedId) {
      getTranscript(embedId)
      .then((transcript) => {
        if (transcript.data) {
          const mergedTranscript = merge(transcript.data.transcriptResponse);
          setTranscript(mergedTranscript);
          setLoading(false);
          setTranscriptState("FOUND");
        }
        if (transcript.data.transcriptResponse.length === 0) {
          setTranscript([]);
          setLoading(false);
          setTranscriptState("NOT_FOUND");
        }
        console.log("state ", transcriptState);
      })
      .catch((err) => {
        setTranscript([]);
        setLoading(false);
        setTranscriptState("NOT_FOUND");
      })
    }
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {!loading ? (<Button 
          variant="containted"
          onClick={(e) => {setLoading(true); handleLoadTranscript()}}
          style={{
            minWidth: '4px',
            backgroundColor: "#e0e0e0",
            textTransform: 'capitalize'
          }}
        >
          Load Transcript
        </Button>) : (
          <CircularProgress />
        )}
      </Box>
    </>
  )
}

function TranscriptViewer({ 
  embedId,
  transcript,
  restElement, setRestElement,
  player,
  bookmark, setBookmark,
  tabValue, setTabValue,
  transcriptTimeoutId, setTranscriptTimeoutId, 
  timeoutId, setTimeoutId // added for the time button to start 
}) {
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState('panel1');
  const scrollContainerRef = useRef(null);
  const [mouseOnTranscript, setMouseOnTranscript] = useState(false);

  const toggleSelect = (index) => {
    if (selected.includes(index)) {
      // Deselect the current index and adjust the range
      const newSelected = [...selected];
      const indexPos = newSelected.indexOf(index);
      if (indexPos === 0 || indexPos === newSelected.length - 1) {
        // Remove only the first or last index
        newSelected.splice(indexPos, 1);
      } else {
        // If a middle item is clicked, remove everything
        newSelected.length = 0;
      }
      setSelected(newSelected);
    } else {
      if (selected.length === 0) {
        // If no items are selected, add the clicked item
        setSelected([index]);
      } else {
        // Add the new index, sort the array, and fill in the range
        const newSelected = [...selected, index];
        newSelected.sort((a, b) => a - b);

        const fullRange = [];
        for (let i = newSelected[0]; i <= newSelected[newSelected.length - 1]; i++) {
          fullRange.push(i);
        }
        setSelected(fullRange);
      }
    }
  };

  const copyToClipboard = (e) => {
    e.stopPropagation();  // Prevents the event from bubbling up to the Paper component
    const selectedText = selected.map(index => transcript[index].text).join(' ');
    setSelected([])
    navigator.clipboard.writeText(selectedText)
      .then(() => alert('Copied to clipboard!'))
      .catch(err => console.error('Failed to copy text: ', err));
  };

  const copyToBookmarks = (e) => {
    e.stopPropagation();  // Prevents the event from bubbling up to the Paper component
    const startTime = transcript[selected[0]].offset;
    const selectedText = selected.map(index => transcript[index].text).join(' ');
    insertTimestamp(
      bookmark, setBookmark, startTime, selectedText
    )

    //TODO tabvalue will be unused in the paper thing (still used for the timelinebar...)
    const sections = getSections(bookmark)
    const sectionIndex = findLastSmallerOffset(bookmark, "time", startTime)
    setTabValue(sectionIndex)

    setSelected([])
  };

  const handleMouseEnter = () => {
    setMouseOnTranscript(true)
    // Stop the transcript timer...
    clearTimeout(transcriptTimeoutId)
    setTimeout(() => {updateTranscriptLocation(false)}, 1)
  };

  const handleMouseLeave = () => {
    // restart the transcript timer...
    setMouseOnTranscript(false)
    updateRestElement()
    updateTranscriptLocation(true)
  };

  const updateRestElement = () => {
    clearTimeout(transcriptTimeoutId);
    if ( player && player.g) {
      if ( transcript.length>0 ) {
        const player_time = player.getCurrentTime();
        let index = findLastSmallerOffset(transcript, "offset", player_time);
        let duration = 0.5;
        if (index+1 < transcript.length){
          duration = transcript[index+1].offset - player_time;
        }
        if ( duration <= 0.5 ) { duration = 0.5; } 
        if ( duration >= 2 ) { duration = 2; } 
        //console.log("transcript duration3: ", transcript.length, "    ", duration, "     ", index, "    ", player_time, "    ", restElement, " ", transcript.text);
        const localTimeoutId = setTimeout(updateRestElement, duration * 1000);
        setTranscriptTimeoutId(localTimeoutId);
        if (index > 0){
          setRestElement(index)
        }
      }
    }
  }


  const updateTranscriptLocation = (limit) => {
    if (scrollContainerRef.current) {
      // Calculate the position to scroll to so that `restElement` is the second item
      const elements = scrollContainerRef.current.children;
      const [start, stop] = transcriptStartStop(limit)
      const startElement = ((restElement - start) > 0) ? (restElement-start) : 0;
      if (elements.length > startElement && startElement > 0) {
        const firstElement = elements[0].getBoundingClientRect().height;
        const targetElement = elements[startElement].getBoundingClientRect().top;
        const scrollTopValue = targetElement - firstElement - elements[0].getBoundingClientRect().top;
        scrollContainerRef.current.scrollTop = scrollTopValue;
      } else {
        // Default behavior to scroll to top
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }

  useEffect(() => {
    updateRestElement()
  }, []); 

  useEffect(() => {
    if ( (transcript.length>0) && !mouseOnTranscript){
      updateTranscriptLocation(!mouseOnTranscript);
    }
  }, [restElement]); 

  const handleStartClick = (time) => { // this function should probably be moved up
    if (timeoutId) { // Clear current timer, player's handleStateChange handle the remaining loop timer management
      clearTimeout(timeoutId);
      setTimeoutId(null);
    } 
    player.seekTo(time);
    player.playVideo();
  }

  const handleButtonClick = (e, index) => {
    e.stopPropagation();  // Prevents the event from bubbling up to the Paper component
    handleStartClick(transcript[index].offset)
  };

  const handleChange = (panel) => (event, isExpanded) => {
    if (expanded === panel) { // the event is closing the transcript
      clearTimeout(transcriptTimeoutId)
    }
    if (expanded === false) { // the event is expanding the transcript
      clearTimeout(transcriptTimeoutId)
      updateRestElement()
      updateTranscriptLocation(!mouseOnTranscript)
    }
    setExpanded(isExpanded ? panel : false);
  };
  
  const transcriptStartStop = (limit) => {
    let start = 0
    let stop = -1
    if ( limit ) {
      start = Math.max(restElement-10, 0)
      stop = Math.min(restElement+10, transcript.length - 1)
    }
    return [start, stop]
  }

  const slicedTranscript = (limit) => {
    const [start, stop] = transcriptStartStop(limit)
    return transcript.slice(start, stop)
  }

  const getPaperColor = (index) => {
    const [start, stop] = transcriptStartStop(!mouseOnTranscript)
    let color = "#ffffff";
    if (selected.includes(index)){
      color = "#f0f0f0";
      if (index === (restElement-start) ){
        color = "#b7bcf2";
      }
    } else {
      if (index === (restElement-start) ){
        color = "#e3e5fa";
      }
    }
    return color;
  }

  return (
    <>
      { transcript.length &&
      <Accordion 
        expanded={expanded === 'panel1'} 
        onChange={handleChange('panel1')} 
        onMouseLeave={handleMouseLeave}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          style={{
            fontWeight: 'bold', // Makes the text bold
            fontSize: '20px' // Sets the font size to 20 pixels
          }}
        >
          <Grid container
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item xs={10} md={10}>
              <StyledH5Heading>Transcript</StyledH5Heading>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Box 
            ref={scrollContainerRef}
            onMouseEnter={handleMouseEnter}
            sx={{ height: '400px', overflowY: 'auto', marginBottom: '20px' }}
          >
            {slicedTranscript(!mouseOnTranscript).map((entry, index) => (
              <Paper 
                key={index}
                onClick={() => toggleSelect(index)}
                elevation={selected.includes(index) ? 3 : 1}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                  margin: 0,
                  backgroundColor: getPaperColor(index),
                  cursor: 'pointer'
                }}
              >
                <Button variant="text" sx={{ marginRight: 2, flexShrink: 0 }}
                  onClick={(e) => handleButtonClick(e, index)}>
                  {getTimestampFromDuration(Math.round(entry.offset.toFixed(2)))}
                </Button>
                <Typography variant="body1" sx={{ flexGrow: 1 }}>{entry.text}</Typography>
                {
                  selected.includes(index) && (
                    <>
                      <IconButton 
                        variant="contained" 
                        onClick={copyToClipboard}
                        color="#ffffff">
                          <FileCopyIcon/>
                      </IconButton>
                      <IconButton 
                        variant="contained" 
                        onClick={copyToBookmarks}
                        color="#ffffff">
                          <EditNoteIcon/>
                      </IconButton>
                    </>
                  )
                }
              </Paper>
            ))}
          </Box>

        </AccordionDetails>
      </Accordion>
      }
    </>
  );
}

export {TranscriptViewer, TranscriptLoader};
